import { z } from 'zod';
import { endpoint } from './endpoint.js';
import {
  QueueEntrySchema,
  JoinQueueBodySchema,
  QueueListResponseSchema,
  QueueListByReservationsQuerySchema,
  QueueListByReservationsResponseSchema,
} from '../schemas/reservation-queue.js';
import { ReservationRecordSchema } from './reservations.endpoints.js';

const QueueEntryIdSchema = z.coerce.number().int().positive();

export const reservationQueue = {
  /**
   * POST /reservations/queue
   * Join the waitlist for a contested media unit window.
   * Guarded by reservation.create permission.
   */
  join: endpoint('POST', '/reservations/queue', JoinQueueBodySchema, QueueEntrySchema),

  /**
   * DELETE /reservations/queue/:id
   * Leave the waitlist (holder self-removes).
   * Guarded by reservation.cancel permission.
   */
  leave: endpoint(
    'DELETE',
    '/reservations/queue/:id',
    z.object({ id: QueueEntryIdSchema }),
    z.void(),
  ),

  /**
   * GET /reservations/queue
   * List the queue for a given media unit and window, ordered FCFS.
   * Guarded by reservation.list permission.
   */
  listPosition: endpoint(
    'GET',
    '/reservations/queue',
    z.object({
      mediaUnitId: z.coerce.number().int().positive(),
      // dateFrom and dateTo are optional: omitting them returns the full queue
      // for the unit across all windows (admin view).
      dateFrom: z.string().optional(),
      dateTo: z.string().optional(),
    }),
    QueueListResponseSchema,
  ),

  /**
   * GET /reservations/queue/by-reservations
   * Batched FCFS queue lookup for the current page of the global reservations
   * list — one round trip instead of N per-reservation lookups. Server
   * correlates by media-unit + date-window overlap (same predicate as
   * `waitingCountSubquery` in @ooh/db) so the client never re-implements it.
   * Guarded by reservation.view permission (same as listPosition).
   */
  listByReservations: endpoint(
    'GET',
    '/reservations/queue/by-reservations',
    QueueListByReservationsQuerySchema,
    QueueListByReservationsResponseSchema,
  ),

  /**
   * POST /reservations/queue/:id/promote
   * Admin displace-and-promote: force-release the active holder and install
   * the chosen queue entry as the new active reservation.
   * Guarded by reservation.manage_waitlist + reservation.force_release.
   */
  adminPromote: endpoint(
    'POST',
    '/reservations/queue/:id/promote',
    z.object({ id: QueueEntryIdSchema }),
    ReservationRecordSchema,
  ),

  // Note: admin entry removal uses the same HTTP endpoint as `leave`
  // (DELETE /reservations/queue/:id). Permission enforcement (manage_waitlist
  // vs self-only) is handled server-side. Callers use `leave` for both.
};
